import { Link } from "react-router-dom";
import Seo from "../components/Seo.jsx";

// Experiences service page. Frontend-only; mirrors the Flights/Stays/Visas
// structure with Experiences-specific copy. CTAs route into the existing
// Plan a Trip intake and Track Trip — no new logic or dependencies.

const ARRANGE = [
  { t: "Safaris", d: "Game drives and lodges timed for the wildlife, with guides who know the land." },
  { t: "Private tours", d: "Just you and a local expert, at your own pace — no crowded coaches." },
  { t: "Cultural experiences", d: "Food, music, markets and history that show you the real place." },
  { t: "Airport transfers", d: "Someone waiting at arrivals, so the trip starts calm." },
  { t: "Special occasions", d: "Honeymoons, birthdays and milestones, quietly made memorable." },
  { t: "Multi-day add-ons", d: "Slot a day or a week of experiences into any trip you're planning." },
];

const STYLES = [
  "Safari", "Culture", "Food", "Beach day", "City tour", "Nightlife",
  "Wellness", "Family", "Romantic", "Luxury", "Adventure", "Private guide",
];

const STEPS = [
  { n: "01", t: "Tell us the moment", d: "What you want to feel, see or celebrate." },
  { n: "02", t: "We curate suitable options", d: "Hand-picked experiences, not a generic list." },
  { n: "03", t: "You confirm timing and price", d: "Clear details and final cost before you pay." },
  { n: "04", t: "We support the experience day", d: "On hand before and during, so it runs smoothly." },
];

export default function Experiences() {
  return (
    <div className="page">
      <Seo
        title="Travel Experiences — 513Travel"
        description="Shape the memorable parts of your trip with safaris, private tours, cultural days, transfers, special occasions and curated experiences."
        path="/experiences"
      />
      {/* ===== Hero ===== */}
      <section className="page-hero hero-split experiences-hero">
        <div className="container">
          <div className="hero-split-copy">
            <div className="eyebrow">Experiences</div>
            <h1>The moments between the flights</h1>
            <p className="lede">
              From safaris and private guides to cultural days, transfers and
              special occasions, 513Travel helps you shape the parts of a trip
              people remember most.
            </p>
            <div className="page-actions">
              <Link to="/plan-a-trip" className="btn gold">Add an experience</Link>
              <Link to="/plan-a-trip" className="btn outline">Plan a full trip</Link>
            </div>
          </div>
          <figure className="hero-figure">
            <img
              src="/experiences-hero.webp"
              alt="Travellers enjoying a curated African sunset experience on a scenic terrace"
              loading="eager"
              decoding="async"
            />
          </figure>
        </div>
      </section>

      {/* ===== What we arrange ===== */}
      <section className="section section--warm">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">What we arrange</div>
            <h2>The parts you'll remember</h2>
            <p>The experiences that turn a trip into a story — curated around you, not pulled off a shelf.</p>
          </div>
          <div className="grid grid-3">
            {ARRANGE.map((c) => (
              <div className="service-card" key={c.t}>
                <h3>{c.t}</h3>
                <p>{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Experience styles ===== */}
      <section className="section">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">Find your moment</div>
            <h2>Experience styles</h2>
            <p>However you like to travel, we shape the day around it — curated and personal, never generic tourism.</p>
          </div>
          <div className="route-chips">
            {STYLES.map((s) => (
              <span className="route-chip" key={s}>{s}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ===== How it works ===== */}
      <section className="section section--warm">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">How it works</div>
            <h2>How experience requests work</h2>
          </div>
          <div className="grid grid-4">
            {STEPS.map((s) => (
              <div className="step-card" key={s.n}>
                <span className="step-num" aria-hidden="true">{s.n}</span>
                <h3>{s.t}</h3>
                <p>{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== Trust note ===== */}
      <section className="section" style={{ paddingTop: 0 }}>
        <div className="container">
          <p className="demo-note demo-note--center">
            Experience availability, pricing and timing can change by season and
            local conditions. We confirm the final details before you pay.
          </p>
        </div>
      </section>

      {/* ===== Final CTA ===== */}
      <section className="section section--green">
        <div className="container">
          <div className="feature-copy feature-solo">
            <div className="eyebrow">Ready to experience</div>
            <h2>Want the trip to feel complete?</h2>
            <p>Tell us what you want to feel, see or celebrate. We'll help shape the experience around your trip.</p>
            <div className="page-actions">
              <Link to="/plan-a-trip" className="btn light">Add an experience</Link>
              <Link to="/track-trip" className="btn outline-light">Track existing trip</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
